<template>
  <div class="px-4 sm:px-6 lg:px-10 py-6">
    <h1 class="text-[26px] font-bold text-gray-900">
      {{ pageTitle }}
    </h1>

    <div v-if="mode==='add'" class="mt-4 mb-8 flex justify-end">
      <button
        class="inline-flex items-center gap-2 h-10 px-5 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-50"
        :style="{ backgroundColor: '#5B9AC5' }"
        :disabled="loading"
        @click="handleSubmit"
      >
        <span v-if="loading">Saving...</span>
        <span v-else>Add Registration Deeds</span>
      </button>
    </div>

    <div v-if="mode==='edit'" class="mt-6 flex justify-end mb-12">
      <button
        class="inline-flex items-center gap-2 h-10 px-6 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-50"
        :style="{ backgroundColor: '#5B9AC5' }"
        :disabled="loading"
        @click="handleSubmit"
      >
        <span v-if="loading">Saving...</span>
        <span v-else>Save Changes</span>
      </button>
    </div>

    <div class="bg-white p-4 sm:p-6 sm:p-8 rounded-2xl shadow-sm">
      <!-- General error banner (for errors not tied to a visible input) -->
      <div
        v-if="generalError"
        class="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm whitespace-pre-line"
      >
        {{ generalError }}
      </div>

      <!-- Note (full width) -->
      <div class="mb-6">
        <div class="text-center text-[16px] font-bold text-gray-800 mb-2">
          Note For Registration Deed
        </div>
        <input
          class="w-full h-[47px] rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none"
          :class="formErrors.note ? 'border-red-500' : ''"
          placeholder="Enter note"
          v-model="model.note"
          :disabled="isView"
        />
        <p v-if="formErrors.note" class="text-[12px] text-red-600 mt-1">{{ formErrors.note }}</p>
      </div>

      <!-- No. / Date -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label class="block text-[14px] font-bold text-gray-800 mb-1">Registration Deed No.</label>
          <input
            class="w-full h-[47px] rounded-xl border-2 border-gray-300 px-3 text-sm bg-gray-50 text-gray-600 focus:outline-none cursor-not-allowed"
            placeholder="Auto-generated sequence"
            v-model="model.number"
            disabled
          />
        </div>
        <div>
          <label class="block text-[14px] font-bold text-gray-800 mb-1">Date</label>
          <input
            type="date"
            class="w-full h-[47px] rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none"
            :class="formErrors.date ? 'border-red-500' : ''"
            v-model="model.date"
            :disabled="isView"
          />
          <p v-if="formErrors.date" class="text-[12px] text-red-600 mt-1">{{ formErrors.date }}</p>
        </div>
      </div>

      <!-- Lines Container with Horizontal Scroll for Responsiveness -->
      <div class="w-full overflow-x-auto pb-4">
        <div class="min-w-[1200px]">
          <!-- Lines -->
          <div class="border rounded-2xl mt-4 p-4 sm:p-5">
            <div
              v-for="(row, idx) in lineItems"
              :key="row._key"
              class="grid gap-3 items-start mb-3"
              :style="rowGridTemplate(row)"
            >
              <!-- No. -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">No.</div>
                <input
                  class="rounded-xl border-2 border-gray-300 text-sm bg-gray-50 text-center"
                  :style="box(60)"
                  :value="idx+1"
                  disabled
                />
              </div>

              <!-- Account Name Searchable Dropdown -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Account Name</div>
                <div class="relative" :style="{ width: '100%', height: '47px' }">
                  <div
                    class="w-full h-full rounded-xl border-2 border-gray-300 px-3 text-sm flex items-center justify-between bg-white transition-colors focus:border-blue-500 outline-none"
                    :class="[
                      isView ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                      (rowError(idx,'account_id') || rowError(idx,'payment_for')) ? 'border-red-500' : ''
                    ]"
                    @click="!isView && toggleDropdown(idx, 'account')"
                  >
                    <span :class="!row.account_id ? 'text-gray-400' : ''" class="truncate">
                      {{ getLabel(accountOptions, row.account_id) || 'Select' }}
                    </span>
                    <span class="text-gray-400 text-xs shrink-0 ml-1">▼</span>
                  </div>

                  <div
                    v-if="openDropdownIndex === idx && openDropdownField === 'account'"
                    class="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg z-50 min-w-[155.7px]"
                  >
                    <input
                      v-model="searchAccount[row._key]"
                      type="text"
                      placeholder="Search..."
                      class="w-full px-3 py-2 border-b text-sm outline-none rounded-t-xl"
                      @keydown="handleSelectKeydown(accountOptions, searchAccount[row._key], (opt) => selectOption(row, 'account_id', opt), $event)"
                      @input="activeOptionIndex = 0"
                      ref="dropdownSearchInput"
                    />
                    <div class="max-h-48 overflow-y-auto" ref="optionsContainer">
                      <div
                        v-for="(opt, oIdx) in filteredOptions(accountOptions, searchAccount[row._key])"
                        :key="opt.value"
                        @click="selectOption(row, 'account_id', opt)"
                        @mouseenter="activeOptionIndex = oIdx"
                        class="px-3 py-2 text-sm cursor-pointer transition-colors"
                        :class="[
                          activeOptionIndex === oIdx ? 'bg-gray-100 font-medium' : '',
                          row.account_id === opt.value ? 'text-blue-600 bg-blue-50/30' : 'text-gray-700'
                        ]"
                      >
                        {{ opt.label }}
                      </div>
                      <div v-if="filteredOptions(accountOptions, searchAccount[row._key]).length === 0" class="px-3 py-3 text-sm text-center text-gray-400">
                        No options found
                      </div>
                    </div>
                  </div>
                </div>
                <p v-if="rowError(idx,'account_id') || rowError(idx,'payment_for')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'account_id') || rowError(idx,'payment_for') }}
                </p>
              </div>

              <!-- Customer Name Searchable Dropdown -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Customer Name</div>
                <div class="relative" :style="{ width: '100%', height: '47px' }">
                  <div
                    class="w-full h-full rounded-xl border-2 border-gray-300 px-3 text-sm flex items-center justify-between bg-white transition-colors focus:border-blue-500 outline-none"
                    :class="[
                      isView ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                      rowError(idx,'customer_id') ? 'border-red-500' : ''
                    ]"
                    @click="!isView && toggleDropdown(idx, 'customer')"
                  >
                    <span :class="!row.customer_id ? 'text-gray-400' : ''" class="truncate">
                      {{ getLabel(customerOptions, row.customer_id) || 'Select' }}
                    </span>
                    <span class="text-gray-400 text-xs shrink-0 ml-1">▼</span>
                  </div>

                  <div
                    v-if="openDropdownIndex === idx && openDropdownField === 'customer'"
                    class="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg z-50 min-w-[155.7px]"
                  >
                    <input
                      v-model="searchCustomer[row._key]"
                      type="text"
                      placeholder="Search..."
                      class="w-full px-3 py-2 border-b text-sm outline-none rounded-t-xl"
                      @keydown="handleSelectKeydown(customerOptions, searchCustomer[row._key], (opt) => selectOption(row, 'customer_id', opt), $event)"
                      @input="activeOptionIndex = 0"
                      ref="dropdownSearchInput"
                    />
                    <div class="max-h-48 overflow-y-auto" ref="optionsContainer">
                      <div
                        v-for="(opt, oIdx) in filteredOptions(customerOptions, searchCustomer[row._key])"
                        :key="opt.value"
                        @click="selectOption(row, 'customer_id', opt)"
                        @mouseenter="activeOptionIndex = oIdx"
                        class="px-3 py-2 text-sm cursor-pointer transition-colors"
                        :class="[
                          activeOptionIndex === oIdx ? 'bg-gray-100 font-medium' : '',
                          row.customer_id === opt.value ? 'text-blue-600 bg-blue-50/30' : 'text-gray-700'
                        ]"
                      >
                        {{ opt.label }}
                      </div>
                      <div v-if="filteredOptions(customerOptions, searchCustomer[row._key]).length === 0" class="px-3 py-3 text-sm text-center text-gray-400">
                        No options found
                      </div>
                    </div>
                  </div>
                </div>
                <p v-if="rowError(idx,'customer_id')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'customer_id') }}
                </p>
              </div>

              <!-- Supplier Name Searchable Dropdown -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Supplier Name</div>
                <div class="relative" :style="{ width: '100%', height: '47px' }">
                  <div
                    class="w-full h-full rounded-xl border-2 border-gray-300 px-3 text-sm flex items-center justify-between bg-white transition-colors focus:border-blue-500 outline-none"
                    :class="[
                      isView ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                      rowError(idx,'supplier_id') ? 'border-red-500' : ''
                    ]"
                    @click="!isView && toggleDropdown(idx, 'supplier')"
                  >
                    <span :class="!row.supplier_id ? 'text-gray-400' : ''" class="truncate">
                      {{ getLabel(supplierOptions, row.supplier_id) || 'Select' }}
                    </span>
                    <span class="text-gray-400 text-xs shrink-0 ml-1">▼</span>
                  </div>

                  <div
                    v-if="openDropdownIndex === idx && openDropdownField === 'supplier'"
                    class="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg z-50 min-w-[155.7px]"
                  >
                    <input
                      v-model="searchSupplier[row._key]"
                      type="text"
                      placeholder="Search..."
                      class="w-full px-3 py-2 border-b text-sm outline-none rounded-t-xl"
                      @keydown="handleSelectKeydown(supplierOptions, searchSupplier[row._key], (opt) => selectOption(row, 'supplier_id', opt), $event)"
                      @input="activeOptionIndex = 0"
                      ref="dropdownSearchInput"
                    />
                    <div class="max-h-48 overflow-y-auto" ref="optionsContainer">
                      <div
                        v-for="(opt, oIdx) in filteredOptions(supplierOptions, searchSupplier[row._key])"
                        :key="opt.value"
                        @click="selectOption(row, 'supplier_id', opt)"
                        @mouseenter="activeOptionIndex = oIdx"
                        class="px-3 py-2 text-sm cursor-pointer transition-colors"
                        :class="[
                          activeOptionIndex === oIdx ? 'bg-gray-100 font-medium' : '',
                          row.supplier_id === opt.value ? 'text-blue-600 bg-blue-50/30' : 'text-gray-700'
                        ]"
                      >
                        {{ opt.label }}
                      </div>
                      <div v-if="filteredOptions(supplierOptions, searchSupplier[row._key]).length === 0" class="px-3 py-3 text-sm text-center text-gray-400">
                        No options found
                      </div>
                    </div>
                  </div>
                </div>
                <p v-if="rowError(idx,'supplier_id')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'supplier_id') }}
                </p>
              </div>

              <!-- Employee Name Searchable Dropdown -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Employee Name</div>
                <div class="relative" :style="{ width: '100%', height: '47px' }">
                  <div
                    class="w-full h-full rounded-xl border-2 border-gray-300 px-3 text-sm flex items-center justify-between bg-white transition-colors focus:border-blue-500 outline-none"
                    :class="[
                      isView ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                      rowError(idx,'employee_id') ? 'border-red-500' : ''
                    ]"
                    @click="!isView && toggleDropdown(idx, 'employee')"
                  >
                    <span :class="!row.employee_id ? 'text-gray-400' : ''" class="truncate">
                      {{ getLabel(employeeOptions, row.employee_id) || 'Select' }}
                    </span>
                    <span class="text-gray-400 text-xs shrink-0 ml-1">▼</span>
                  </div>

                  <div
                    v-if="openDropdownIndex === idx && openDropdownField === 'employee'"
                    class="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg z-50 min-w-[155.7px]"
                  >
                    <input
                      v-model="searchEmployee[row._key]"
                      type="text"
                      placeholder="Search..."
                      class="w-full px-3 py-2 border-b text-sm outline-none rounded-t-xl"
                      @keydown="handleSelectKeydown(employeeOptions, searchEmployee[row._key], (opt) => selectOption(row, 'employee_id', opt), $event)"
                      @input="activeOptionIndex = 0"
                      ref="dropdownSearchInput"
                    />
                    <div class="max-h-48 overflow-y-auto" ref="optionsContainer">
                      <div
                        v-for="(opt, oIdx) in filteredOptions(employeeOptions, searchEmployee[row._key])"
                        :key="opt.value"
                        @click="selectOption(row, 'employee_id', opt)"
                        @mouseenter="activeOptionIndex = oIdx"
                        class="px-3 py-2 text-sm cursor-pointer transition-colors"
                        :class="[
                          activeOptionIndex === oIdx ? 'bg-gray-100 font-medium' : '',
                          row.employee_id === opt.value ? 'text-blue-600 bg-blue-50/30' : 'text-gray-700'
                        ]"
                      >
                        {{ opt.label }}
                      </div>
                      <div v-if="filteredOptions(employeeOptions, searchEmployee[row._key]).length === 0" class="px-3 py-3 text-sm text-center text-gray-400">
                        No options found
                      </div>
                    </div>
                  </div>
                </div>
                <p v-if="rowError(idx,'employee_id')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'employee_id') }}
                </p>
              </div>

              <!-- Debit -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Debit</div>
                <input
                  type="number"
                  step="any"
                  class="rounded-xl border-2 border-gray-300 text-sm px-3 focus:outline-none focus:border-blue-500"
                  :class="rowError(idx,'debit') ? 'border-red-500' : ''"
                  :style="box(155.7)"
                  v-model.number="row.debit"
                  :disabled="isView"
                  placeholder="enter debit amount"
                  @wheel="onNumberWheel"
                />
                <p v-if="rowError(idx,'debit')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'debit') }}
                </p>
              </div>

              <!-- Credit -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Credit</div>
                <input
                  type="number"
                  step="any"
                  class="rounded-xl border-2 border-gray-300 text-sm px-3 focus:outline-none focus:border-blue-500"
                  :class="rowError(idx,'credit') ? 'border-red-500' : ''"
                  :style="box(155.7)"
                  v-model.number="row.credit"
                  :disabled="isView"
                  placeholder="enter credit amount"
                  @wheel="onNumberWheel"
                />
                <p v-if="rowError(idx,'credit')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'credit') }}
                </p>
              </div>

              <!-- Note -->
              <div class="flex flex-col gap-1 min-w-0">
                <div class="text-[13px] font-medium text-gray-600 truncate leading-tight">Note</div>
                <input
                  class="rounded-xl border-2 border-gray-300 text-sm px-3 focus:outline-none focus:border-blue-500"
                  :class="rowError(idx,'note') ? 'border-red-500' : ''"
                  :style="box(250)"
                  placeholder="Enter note"
                  v-model="row.note"
                  :disabled="isView"
                />
                <p v-if="rowError(idx,'note')" class="text-[11px] text-red-600 mt-1 leading-tight">
                  {{ rowError(idx,'note') }}
                </p>
              </div>
            </div>

            <!-- totals -->
            <div
              class="grid gap-3 mt-4 items-center"
              :style="gridTemplate"
            >
              <div style="width:60px;"></div>
              <div style="width:155.7px;"></div>
              <div style="width:155.7px;"></div>
              <div style="width:155.7px;"></div>

              <!-- col 5 -->
              <div class="text-right font-semibold text-gray-700">Total</div>

              <!-- col 6: under Debit -->
              <input
                class="rounded-xl border-2 border-gray-300 text-sm bg-gray-50 px-3 text-right font-bold"
                :class="Math.abs(totals.debit - totals.credit) > 0.01 ? 'border-red-500 text-red-600' : ''"
                :style="box(155.7)"
                :value="totals.debit.toFixed(2)"
                disabled
              />
              <!-- col 7: under Credit -->
              <input
                class="rounded-xl border-2 border-gray-300 text-sm bg-gray-50 px-3 text-right font-bold"
                :class="Math.abs(totals.debit - totals.credit) > 0.01 ? 'border-red-500 text-red-600' : ''"
                :style="box(155.7)"
                :value="totals.credit.toFixed(2)"
                disabled
              />
              <!-- col 8: empty (Note column) -->
              <div :style="box(250)"></div>
            </div>

            <div v-if="!isView" class="flex items-center justify-center gap-6 mt-5">
              <button
                type="button"
                class="w-10 h-10 rounded-full border border-gray-300 hover:bg-gray-50 flex items-center justify-center leading-none"
                @click="removeLine"
                :disabled="lineItems.length <= 1"
              >
                <span class="text-3xl transform -translate-y-1">−</span>
              </button>

              <button
                type="button"
                class="w-10 h-10 rounded-full border border-gray-300 hover:bg-gray-50 flex items-center justify-center leading-none"
                @click="addLine"
              >
                <span class="text-3xl transform -translate-y-1">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import Swal from "sweetalert2";

export default {
  name: "RegistrationDeedForm",
  props: {
    mode: { type: String, default: "add" },
    deedId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      model: {
        note: "",
        number: "",
        date: new Date().toISOString().split('T')[0],
        company_id: null
      },
      lineItems: [ mkLine(), mkLine() ],
      totals: { debit: 0, credit: 0 },

      accountOptions: [],
      customerOptions: [],
      supplierOptions: [],
      employeeOptions: [],

      openDropdownIndex: null,
      openDropdownField: null,
      searchAccount: {},
      searchCustomer: {},
      searchSupplier: {},
      searchEmployee: {},
      activeOptionIndex: 0,

      formErrors: {},
      rowErrors: {},
      generalError: "",
    };
  },
  computed: {
    isView() { return String(this.mode).toLowerCase() === 'view'; },
    pageTitle() {
      if (this.mode === 'add')  return 'Add Registration Deeds';
      if (this.mode === 'edit') return 'Edit Registration Deeds';
      return 'View Registration Deeds';
    },
    gridTemplate() {
      return {
        gridTemplateColumns:
          '60px 155.7px 155.7px 155.7px 155.7px 155.7px 155.7px 250px'
      };
    },
  },
  watch: {
    lineItems: {
      deep: true,
      handler() { this.recalcTotals(); },
    },
  },
  async created() {
    this.recalcTotals();
    await this.fetchMeta();

    if (this.mode === 'add') {
      const qCompanyId = this.$route.query.company_id;
      if (qCompanyId) {
        this.model.company_id = qCompanyId;
      }
    }

    if (this.mode !== 'add' && this.deedId) {
      await this.fetchDeedDetails();
    }
  },
  methods: {
    box(w) { return { width: `${w}px`, height: '47px' }; },

    onNumberWheel(e) {
      e.target.blur();
    },

    rowError(idx, field) {
      const r = this.rowErrors[idx];
      return (r && r[field]) || "";
    },

    clearErrors() {
      this.formErrors = {};
      this.rowErrors = {};
      this.generalError = "";
    },

    applyServerErrors(errors) {
      const formErrors = {};
      const rowErrors = {};
      const general = [];

      Object.keys(errors || {}).forEach((key) => {
        const val = errors[key];
        const msg = Array.isArray(val) ? val[0] : val;

        const m = key.match(/^rows\.(\d+)\.(.+)$/);
        if (m) {
          const idx = Number(m[1]);
          const field = m[2];
          if (!rowErrors[idx]) rowErrors[idx] = {};
          rowErrors[idx][field] = msg;
        } else if (key === 'note' || key === 'date') {
          formErrors[key] = msg;
        } else {
          general.push(msg);
        }
      });

      this.formErrors = formErrors;
      this.rowErrors = rowErrors;
      this.generalError = general.join('\n');
    },

    rowGridTemplate(row) {
      const GROUP = 622.8;
      const keys = ['account_id', 'customer_id', 'supplier_id', 'employee_id'];
      const filled = keys.filter(
        (k) => row[k] !== '' && row[k] !== null && row[k] !== undefined
      );

      let dropCols;
      if (filled.length === 1) {
        const wide = 300;
        const narrow = (GROUP - wide) / 3;
        dropCols = keys
          .map((k) => (k === filled[0] ? wide : narrow) + 'px')
          .join(' ');
      } else {
        dropCols = '155.7px 155.7px 155.7px 155.7px';
      }

      return {
        gridTemplateColumns: `60px ${dropCols} 155.7px 155.7px 250px`,
      };
    },

    addLine() { this.lineItems.push(mkLine()); },
    removeLine() { if (this.lineItems.length > 1) this.lineItems.pop(); },

    recalcTotals() {
      const d = this.lineItems.reduce((s, r) => s + (Number(r.debit)  || 0), 0);
      const c = this.lineItems.reduce((s, r) => s + (Number(r.credit) || 0), 0);
      // Round to 2 decimal places for consistency with display
      this.totals.debit = Math.round(d * 100) / 100;
      this.totals.credit = Math.round(c * 100) / 100;
    },

    async fetchMeta() {
      try {
        const res = await api.get("/accounting/registration-deeds/meta");
        const data = res.data || {};

        this.accountOptions = (data.accounts || []).map(item => ({
          value: item.id,
          label: item.name || item.number || `Account #${item.id}`
        }));

        this.customerOptions = (data.customers || []).map(item => ({
          value: item.id,
          label: item.full_name || `Customer #${item.id}`
        }));

        this.supplierOptions = (data.suppliers || []).map(item => ({
          value: item.id,
          label: item.supplier_name || `Supplier #${item.id}`
        }));

        this.employeeOptions = (data.employees || []).map(item => ({
          value: item.id,
          label: item.name || item.username || `Employee #${item.id}`
        }));
      } catch (err) {
        console.error("Failed to fetch meta data:", err);
      }
    },

    async fetchDeedDetails() {
      try {
        this.loading = true;
        const res = await api.get(`/accounting/registration-deeds/${this.deedId}`);
        const { registration_deed, rows } = res.data || {};

        if (registration_deed) {
          this.model.number = registration_deed.company_sequence ?? registration_deed.number ?? "";
          this.model.date = registration_deed.date || "";
          this.model.note = registration_deed.note || "";
          this.model.company_id = registration_deed.company_id || null;
        }

        if (Array.isArray(rows) && rows.length > 0) {
          this.lineItems = rows.map(r => {
            const line = mkLine();
            line.debit = Number(r.debit) || 0;
            line.credit = Number(r.credit) || 0;
            line.note = r.note || "";

            const paymentFor = r.payment_for;
            const paymentForId = r.payment_for_id;

            if (paymentFor === "account" || r.account_id) {
              line.account_id = paymentForId || r.account_id || "";
            } else if (paymentFor === "customer" || r.customer_id) {
              line.customer_id = paymentForId || r.customer_id || "";
            } else if (paymentFor === "supplier" || r.supplier_id) {
              line.supplier_id = paymentForId || r.supplier_id || "";
            } else if (paymentFor === "employee" || r.employee_id) {
              line.employee_id = paymentForId || r.employee_id || "";
            }
            return line;
          });
        }
      } catch (err) {
        console.error("Failed to load deed details:", err);
      } finally {
        this.loading = false;
      }
    },

    async handleSubmit() {
      if (this.isView) return;

      this.clearErrors();

      if (Math.abs(this.totals.debit - this.totals.credit) > 0.01) {
        await Swal.fire({
          icon: 'error',
          title: 'Unbalanced Totals',
          text: 'Debit and Credit totals must be strictly equal!',
          confirmButtonText: 'OK'
        });
        return;
      }

      const formattedRows = this.lineItems.map(row => {
        const accountId = (row.account_id !== "" && row.account_id !== null && row.account_id !== undefined) ? row.account_id : null;
        const customerId = accountId ? null : ((row.customer_id !== "" && row.customer_id !== null && row.customer_id !== undefined) ? row.customer_id : null);
        const supplierId = (accountId || customerId) ? null : ((row.supplier_id !== "" && row.supplier_id !== null && row.supplier_id !== undefined) ? row.supplier_id : null);
        const employeeId = (accountId || customerId || supplierId) ? null : ((row.employee_id !== "" && row.employee_id !== null && row.employee_id !== undefined) ? row.employee_id : null);

        return {
          debit: Number(row.debit) || 0,
          credit: Number(row.credit) || 0,
          note: row.note || null,
          account_id: accountId,
          customer_id: customerId,
          supplier_id: supplierId,
          employee_id: employeeId
        };
      });

      const payload = {
        company_id: String(this.model.company_id || ""),
        date: this.model.date,
        note: this.model.note || null,
        rows: formattedRows
      };

      try {
        this.loading = true;
        if (this.mode === 'add') {
          await api.post('/accounting/registration-deeds', payload);
        } else if (this.mode === 'edit') {
          await api.put(`/accounting/registration-deeds/${this.deedId}`, payload);
        }

        await Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: this.mode === 'add' ? 'Registration deed created successfully.' : 'Registration deed updated successfully.',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });

        this.$router.push({ name: "RegistrationDeeds" });

      } catch (err) {
        console.error("Failed to save registration deed:", err);
        const errMsgs = err.response?.data?.errors;

        if (errMsgs) {
          this.applyServerErrors(errMsgs);

          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'Please fix the highlighted fields',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true
          });
        } else {
          const message = err.response?.data?.message || 'Error occurred while saving';
          this.generalError = message;

          await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: message,
            confirmButtonText: 'OK'
          });
        }
      } finally {
        this.loading = false;
      }
    },

    toggleDropdown(index, field) {
      if (this.openDropdownIndex === index && this.openDropdownField === field) {
        this.openDropdownIndex = null;
        this.openDropdownField = null;
      } else {
        this.openDropdownIndex = index;
        this.openDropdownField = field;
        this.activeOptionIndex = 0;
        this.$nextTick(() => {
          if (this.$refs.dropdownSearchInput && this.$refs.dropdownSearchInput[0]) {
            this.$refs.dropdownSearchInput[0].focus();
          }
        });
      }
    },

    selectOption(row, key, opt) {
      row.account_id = "";
      row.customer_id = "";
      row.supplier_id = "";
      row.employee_id = "";

      row[key] = opt.value ?? opt;

      this.openDropdownIndex = null;
      this.openDropdownField = null;
    },

    getLabel(options, value) {
      const found = (options || []).find(o => (o.value ?? o) === value);
      return found ? (found.label ?? found) : '';
    },

    filteredOptions(options, searchTerm) {
      const term = (searchTerm || '').toLowerCase();
      return (options || []).filter(o =>
        (o.label ?? o).toLowerCase().includes(term)
      );
    },

    handleSelectKeydown(options, searchTerm, selectCallback, event) {
      const opts = this.filteredOptions(options, searchTerm);
      if (opts.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeOptionIndex = (this.activeOptionIndex + 1) % opts.length;
        this.scrollActiveOptionIntoView();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeOptionIndex = (this.activeOptionIndex - 1 + opts.length) % opts.length;
        this.scrollActiveOptionIntoView();
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const selectedOpt = opts[this.activeOptionIndex];
        if (selectedOpt) {
          selectCallback(selectedOpt);
        }
      } else if (event.key === 'Escape') {
        this.openDropdownIndex = null;
        this.openDropdownField = null;
      }
    },

    scrollActiveOptionIntoView() {
      this.$nextTick(() => {
        const container = this.$refs.optionsContainer ? this.$refs.optionsContainer[0] : null;
        if (!container) return;
        const activeEl = container.children[this.activeOptionIndex];
        if (!activeEl) return;

        const containerTop    = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elemTop         = activeEl.offsetTop;
        const elemBottom      = elemTop + activeEl.clientHeight;

        if (elemTop < containerTop) {
          container.scrollTop = elemTop;
        } else if (elemBottom > containerBottom) {
          container.scrollTop = elemBottom - container.clientHeight;
        }
      });
    },
  },
};

function mkLine() {
  return { _key: rid(), account_id:"", customer_id:"", supplier_id:"", employee_id:"", debit:0, credit:0, note:"" };
}
function rid() {
  try { return crypto.getRandomValues(new Uint32Array(1))[0].toString(16); }
  catch { return Math.random().toString(16).slice(2); }
}
</script>

<style scoped>
/* SweetAlert2 buttons — override Tailwind preflight reset */
.swal2-popup .swal2-styled.swal2-confirm {
  background-color: #5B9AC5 !important;
  background-image: none !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}
.swal2-popup .swal2-styled.swal2-confirm:hover {
  background-color: #4e88b0 !important;
}

.swal2-popup .swal2-styled.swal2-cancel {
  background-color: #d33 !important;
  background-image: none !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}
.swal2-popup .swal2-styled.swal2-cancel:hover {
  background-color: #c02a2a !important;
}

.swal2-popup .swal2-styled.swal2-deny {
  background-color: #6e7881 !important;
  background-image: none !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}

.swal2-popup .swal2-styled:focus {
  box-shadow: none !important;
  outline: none !important;
}
</style>
